import store from "@/store";
import { Option } from "@/models/form";

export default function useFormHelpers(): any {
  function yearOptions(n: any) {
    const currentYear = new Date().getFullYear();
    const years = [] as number[];
    for (let i = 0; i < n; i++) {
      const year = currentYear - i;
      years.push(year);
    }

    return years.map((year: number) => {
      return {
        label: year,
        value: year,
      };
    });
  }
  function convertDateOfBirthToAge(dobInput: string) {
    const birthDate = new Date(dobInput);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    const dayDiff = today.getDate() - birthDate.getDate();

    // Adjust age if the birthday hasn't happened yet this year
    // monthDiff < 0 indicates future months that have not happened yet
    // monthDiff === 0 && dayDiff <= 0 indicates future dates in the same month that havent happened yet
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff <= 0)) {
      age--;
    }

    // Check for leap year
    function isLeapYear(year: number) {
      return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    }

    // Adjust for leap year if the birthday is on February 29th
    if (
      birthDate.getMonth() === 1 &&
      birthDate.getDate() === 29 &&
      !isLeapYear(today.getFullYear())
    ) {
      age++; // Add one year if the current year is not a leap year
    }

    return age;
  }
  function documentStatusReasonsOptions(status: string) {
    let documentStatusReasonsOptions =
      store?.state.types["DocumentStatusReasons"]?.results;

    if (status === "Rejected") {
      return (documentStatusReasonsOptions = store?.state.types[
        "DocumentStatusReasons"
      ]?.results
        .filter(
          (dsr: any) =>
            dsr.name !== "Unique Document Type" &&
            dsr.name !== "Unsure of Document Information" &&
            dsr.name !== "Partially Usable Document" &&
            dsr.name !== "Other"
        )
        .map((o: any) => {
          return {
            label: o.name,
            value: o.id,
          } as Option;
        }));
    }

    if (status === "Escalated for Manager Review") {
      return (documentStatusReasonsOptions = store?.state.types[
        "DocumentStatusReasons"
      ]?.results
        .filter(
          (dsr: any) =>
            dsr.name !== "Unreadable / Blurry" &&
            dsr.name !== "Incomplete Image" &&
            dsr.name !== "Not Usable for Verification" &&
            dsr.name !== "Data Incomplete"
        )
        .map((o: any) => {
          return {
            label: o.name,
            value: o.id,
          } as Option;
        }));
    }
  }

  function generatePatch(original, modified) {
    // If either isn't an object, or if they are null, do a direct comparison
    if (
      typeof original !== "object" ||
      original === null ||
      typeof modified !== "object" ||
      modified === null
    ) {
      return original === modified ? undefined : modified;
    }

    // If we are dealing with arrays, a full replacement patch is safest for simple setups
    if (Array.isArray(original) || Array.isArray(modified)) {
      return JSON.stringify(original) === JSON.stringify(modified)
        ? undefined
        : modified;
    }

    const patch = {};
    let hasChanges = false;

    // Get all unique keys from both objects
    const allKeys = new Set([
      ...Object.keys(original),
      ...Object.keys(modified),
    ]);

    for (const key of allKeys) {
      // Scenario 1: Key deleted in modified
      if (!(key in modified)) {
        patch[key] = undefined; // mark as undefined or track deletions explicitly
        hasChanges = true;
      }
      // Scenario 2: Key added in modified
      else if (!(key in original)) {
        patch[key] = modified[key];
        hasChanges = true;
      }
      // Scenario 3: Key exists in both, deep compare them
      else {
        const diff = generatePatch(original[key], modified[key]);
        if (diff !== undefined) {
          patch[key] = diff;
          hasChanges = true;
        }
      }
    }

    return hasChanges ? patch : undefined;
  }

  function buildJsonPatch(original, modified, path = "") {
    let patch = [] as any[];

    for (const key in modified) {
      const currentPath = `${path}/${key}`;
      const leftVal = original[key];
      const rightVal = modified[key];

      // If it's an object and not a null value, recurse
      if (
        rightVal !== null &&
        typeof rightVal === "object" &&
        leftVal !== undefined
      ) {
        patch = patch.concat(buildJsonPatch(leftVal, rightVal, currentPath));
      }
      // Otherwise, check if it's a new or updated value
      else if (JSON.stringify(leftVal) !== JSON.stringify(rightVal)) {
        patch.push({
          op: leftVal === undefined ? "add" : "replace",
          path: currentPath,
          value: rightVal,
        });
      }
    }

    return patch;
  }

  return {
    convertDateOfBirthToAge,
    documentStatusReasonsOptions,
    yearOptions,
    generatePatch,
  };
}
